import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { BULLETIN_TOKEN, REDIRECT_PATH } from "../../utils/constants";
import { signInWithRedirect } from "aws-amplify/auth";
import { FullPageLoading } from "../../component/callback/Callback";
import { useQuery } from "@tanstack/react-query";
import { getSalesNewsMailLink, getSalesNewsMailLinkAuth } from "../../service/SalesNews";
import useUser from "../../hooks/useUser";
import ErrorHandler from "../../component/errors/ErrorHandler";
import { usePushToDataLayer } from "../../hooks/usePushToDataLayer";
import { GTM_EVENTS } from "../../utils/constants";
import { trackThenRedirect, decodeJwtPayload, getCleanURL } from "../../utils/Utils";

const ViewBulletin = () => {
	// const [loading, setLoading] = useState(true);
	const [isError, setIsError] = useState(false);
	const token = new URLSearchParams(window.location.search).get("token");
	const loc = useLocation();
	const { isLoggedIn } = useUser();
	const pushToDL = usePushToDataLayer();
	const tracked = useRef(false);
	// Boot GA4 early - run while the backend is still validating the JWT.
	useEffect(() => {
		const { internal } = decodeJwtPayload(token);
		if (!internal) window.dataLayer?.push({ event: GTM_EVENTS.GA_BOOT, page_url: getCleanURL() })
	}, []);

	const {
		data: pdfLink,
		error: salesNewsError,
		isFetching,
	} = useQuery({
		enabled: !isLoggedIn,
		queryKey: [token, "withoutAuth"],
		queryFn: async () => getSalesNewsMailLink(token),
		retry: (failureCount, error) => {

			if (error?.response?.status === 401 && error?.response?.data?.message?.name ==="TokenExpiredError") {
				return false;
			}
			return failureCount < 3;
		},
	});
	const {
		data: pdfLinkAuth,
		error: salesNewsAuthError,
		isFetchingAuth,
	} = useQuery({
		enabled: isLoggedIn,
		queryKey: [token, "Auth"],
		queryFn: async () => getSalesNewsMailLinkAuth(token),
		retry: (failureCount, error) => {
			if (error?.response?.status === 401 && error?.response?.data?.message?.name ==="TokenExpiredError") {
				return false;
			}
			return failureCount < 3;
		},
	});

	useEffect(() => {
		if (Boolean(pdfLink)) {
			const { redirectUrl, sapAccountId, cust_group_code, internal, bulletin } = pdfLink;
			if (!redirectUrl) {
				localStorage.setItem(BULLETIN_TOKEN, token);
				localStorage.setItem(REDIRECT_PATH, loc.pathname);
				signInWithRedirect();
			} else {
				if (sapAccountId && !internal && !tracked.current) {
					tracked.current = true;
					trackThenRedirect(
						pushToDL,
						GTM_EVENTS.SALES_NEWS_VIEW,
						{
							sap_account_id: sapAccountId,
							...(cust_group_code && { sap_customer_group: cust_group_code }),
							search_criteria: {
								sales_news_nos: [bulletin]
							},
						},
						true,
						redirectUrl
					);
				} else {
					window.location.href = redirectUrl;
				}
			}
		}
	}, [pdfLink]);

	useEffect(() => {
		if (Boolean(pdfLinkAuth)) {
			const { redirectUrl, sapAccountId, cust_group_code, internal, bulletin } = pdfLinkAuth;

			if (!redirectUrl) {
				setIsError(true);
			} else {
				if (sapAccountId && !internal && !tracked.current) {
					tracked.current = true;
					trackThenRedirect(
						pushToDL,
						GTM_EVENTS.SALES_NEWS_VIEW,
						{
							sap_account_id: sapAccountId,
							...(cust_group_code && { sap_customer_group: cust_group_code }),
							search_criteria: {
								sales_news_nos: [bulletin]
							},
						},
						true,
						redirectUrl
					);
				} else {
					window.location.href = redirectUrl;
				}
			}
		}
	}, [pdfLinkAuth]);

	
	if (salesNewsError?.response?.data?.message?.name ==="TokenExpiredError" || salesNewsAuthError?.response?.data?.message?.name ==="TokenExpiredError") {
		return <ErrorHandler title={"Bulletin Expired"} desc={"Unable to Load Bulletin"} />;
	}
	if (isError || salesNewsError || salesNewsAuthError) {
		return <ErrorHandler title={"Something is wrong"} desc={"Unable to Load Bulletin"} />;
	}

	return <FullPageLoading isError={false}/>;
};

export default ViewBulletin;
